import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Checkbox } from 'src/@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/@/components/ui/select';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import propostasService from 'src/services/propostasService';
import DocumentCanvas from './editor/DocumentCanvas';
import VisualizadorDocumento from './editor/VisualizadorDocumento';
import AgenteIaPainel from './editor/AgenteIaPainel';
import CanvasEditor from './editor/canvas/CanvasEditor';
import CanvasReadOnly from './editor/canvas/CanvasReadOnly';
import {
  blocosParaCanvas,
  documentoIaParaCanvas,
  adicionarTextoAoCanvas,
  corDeFundoCanvas,
  aplicarCorDeFundo,
} from './editor/canvas/canvasUtils';
import { PROPOSTAS_DESIGN } from './Propostas.design';
import { CATEGORIAS_MODELO } from './modelosPreProntosData';

const DEFAULT_TEMPLATE = {
  nome: 'Novo modelo de proposta',
  descricao: '',
  categoria: 'geral',
  layout: {
    tema_cor: '#7C3AED',
    logo_url: '',
    empresa: { nome: '', cnpj: '', telefone: '', email: '', endereco: '' },
    rodape_texto: ''
  },
  blocos: [
    { tipo: 'data', titulo: '', conteudo_html: '', config: { cidade: '' }, ordem: 0 },
    { tipo: 'dados_cliente', titulo: '', conteudo_html: '', config: {}, ordem: 1 },
    { tipo: 'texto', titulo: 'Apresentação', conteudo_html: 'Olá {{contato.nome}}, segue nossa proposta comercial para {{lead.nome}}.', config: {}, ordem: 2 },
    { tipo: 'itens', titulo: '', conteudo_html: '', config: {}, ordem: 3 },
    { tipo: 'forma_pagamento', titulo: 'Forma de pagamento', conteudo_html: '', config: {}, ordem: 4 },
    { tipo: 'validade', titulo: '', conteudo_html: '', config: {}, ordem: 5 },
    { tipo: 'observacoes', titulo: 'Observações', conteudo_html: '', config: {}, ordem: 6 }
  ],
  itens_padrao: [{ descricao: '', quantidade: 1, preco_unitario: 0, desconto_percent: 0 }],
  validade_dias_default: 15,
  config_automacao: {
    ao_enviar: { mover_estagio: false },
    ao_aceitar: { mover_estagio: false, atualizar_valor_lead: true }
  }
};

export default function ModeloEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const isEditing = Boolean(id);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [chatAberto, setChatAberto] = useState(false);

  useEffect(() => {
    if (isEditing) loadTemplate();
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await propostasService.buscarTemplate(id);
      if (!response.error) setTemplate(response.data);
    } catch (error) {
      console.error('Erro ao carregar modelo:', error);
      toast({ title: 'Erro', description: 'Não foi possível carregar o modelo', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Adapta o shape do template (nome/itens_padrao/validade_dias_default) para o shape que
  // DocumentCanvas espera (titulo/itens/validade_dias).
  const proposta = {
    titulo: template.nome,
    contato: { nome: '{{contato.nome}}' },
    layout: template.layout,
    blocos: template.blocos,
    modo_design: template.modo_design,
    canvas: template.canvas,
    itens: template.itens_padrao,
    validade_dias: template.validade_dias_default,
    desconto_global_percent: 0
  };

  const handleChangeCanvas = (patch) => {
    setTemplate(prev => ({
      ...prev,
      ...(patch.titulo !== undefined && { nome: patch.titulo }),
      ...(patch.layout !== undefined && { layout: patch.layout }),
      ...(patch.blocos !== undefined && { blocos: patch.blocos }),
      ...(patch.modo_design !== undefined && { modo_design: patch.modo_design }),
      ...(patch.canvas !== undefined && { canvas: patch.canvas }),
      ...(patch.itens !== undefined && { itens_padrao: patch.itens }),
      ...(patch.validade_dias !== undefined && { validade_dias_default: patch.validade_dias })
    }));
  };

  // Conversa com o agente de IA, igual à da proposta — só que aplicada ao MODELO:
  // o backend recebe `estado.contexto = 'modelo'` e orienta o agente a usar variáveis
  // ({{contato.nome}} etc.) em vez de inventar dados de um cliente. O histórico fica
  // em template.mensagens_ia e é salvo junto com o modelo.
  const handleGerarComIa = async (instrucao) => {
    const mensagemUsuario = { role: 'user', texto: instrucao || '(gerar a partir do contexto)', data: new Date().toISOString() };

    try {
      const response = await propostasService.gerarComIa({
        instrucao,
        historico: (template.mensagens_ia || []).map(m => ({ role: m.role, texto: m.texto })),
        estado: {
          contexto: 'modelo',
          titulo: template.nome || '',
          itens: (template.itens_padrao || []).map(i => ({ descricao: i.descricao, quantidade: i.quantidade, preco_unitario: i.preco_unitario })),
          modo_design: template.modo_design || 'blocos',
          fundo_cor: template.modo_design === 'canvas' ? corDeFundoCanvas(template.canvas) : undefined,
          tem_logo: Boolean(template.layout?.logo_url)
        }
      });
      if (response.error) throw new Error(response.message || 'Não foi possível gerar o modelo com IA');

      const { mensagem, titulo, itens, observacoes, forma_pagamento, visual, documento } = response.data || {};

      setTemplate(prev => {
        const next = { ...prev };
        if (titulo) next.nome = titulo;
        if (itens?.length) next.itens_padrao = itens;

        const aplicouDocumento = Boolean(documento?.secoes?.length && prev.modo_design === 'canvas');
        if (aplicouDocumento) {
          next.canvas = documentoIaParaCanvas(documento, {
            titulo: titulo || prev.nome,
            fundoCor: visual?.fundo_cor || corDeFundoCanvas(prev.canvas),
            temaCor: prev.layout?.tema_cor,
            logoUrl: prev.layout?.logo_url,
            empresaNome: prev.layout?.empresa?.nome,
            assinaturaNome: user?.nomeFantasia || user?.nome,
            // No modelo o bloco de dados do cliente entra sempre — ele se resolve com
            // os dados reais quando a proposta for criada a partir daqui.
            temContato: true
          });
        } else if (visual?.fundo_cor && prev.modo_design === 'canvas' && prev.canvas?.paginas?.length) {
          next.canvas = aplicarCorDeFundo(prev.canvas, visual.fundo_cor);
        }

        if (!aplicouDocumento && (observacoes || forma_pagamento)) {
          if (prev.modo_design === 'canvas') {
            let canvasAtualizado = prev.canvas;
            if (observacoes) canvasAtualizado = adicionarTextoAoCanvas(canvasAtualizado, { titulo: 'Observações', html: observacoes });
            if (forma_pagamento) canvasAtualizado = adicionarTextoAoCanvas(canvasAtualizado, { titulo: 'Forma de pagamento', html: forma_pagamento });
            next.canvas = canvasAtualizado;
          } else {
            const blocosAtualizados = [...(prev.blocos || [])];
            const upsertBloco = (tipo, tituloBloco, html) => {
              const idx = blocosAtualizados.findIndex(b => b.tipo === tipo);
              if (idx >= 0) blocosAtualizados[idx] = { ...blocosAtualizados[idx], conteudo_html: html };
              else blocosAtualizados.push({ tipo, titulo: tituloBloco, conteudo_html: html, config: {}, ordem: blocosAtualizados.length });
            };
            if (observacoes) upsertBloco('observacoes', 'Observações', observacoes);
            if (forma_pagamento) upsertBloco('forma_pagamento', 'Forma de pagamento', forma_pagamento);
            next.blocos = blocosAtualizados;
          }
        }

        const resumo = mensagem?.trim()
          || (itens?.length || titulo || observacoes || forma_pagamento
            ? 'Pronto! Atualizei o modelo. Revise antes de usá-lo em propostas reais.'
            : 'Não encontrei informações suficientes — descreva as seções, os itens padrão e os valores de referência do modelo.');

        next.mensagens_ia = [...(prev.mensagens_ia || []), mensagemUsuario, { role: 'assistant', texto: resumo, data: new Date().toISOString() }];
        return next;
      });
    } catch (error) {
      setTemplate(prev => ({
        ...prev,
        mensagens_ia: [...(prev.mensagens_ia || []), mensagemUsuario, { role: 'assistant', texto: propostasService.mensagemErroGerarIa(error, 'Não foi possível gerar o modelo com IA'), erro: true, data: new Date().toISOString() }]
      }));
      throw error;
    }
  };

  const modoCanvas = template.modo_design === 'canvas';

  const handleToggleDesignMode = () => {
    if (modoCanvas) {
      if (window.confirm('Voltar para o formato em blocos? O design livre fica salvo, mas o modelo passa a usar os blocos novamente.')) {
        setTemplate(prev => ({ ...prev, modo_design: 'blocos' }));
      }
      return;
    }
    if (window.confirm('Mudar para o design livre? Os blocos atuais viram elementos arrastáveis na página.')) {
      setTemplate(prev => ({
        ...prev,
        modo_design: 'canvas',
        canvas: prev.canvas?.paginas?.length ? prev.canvas : blocosParaCanvas(proposta)
      }));
    }
  };

  const handleSave = async () => {
    if (!template.nome?.trim()) {
      toast({ title: 'Atenção', description: 'Informe um nome para o modelo', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: template.nome,
        descricao: template.descricao,
        categoria: template.categoria || 'geral',
        layout: template.layout,
        blocos: template.blocos,
        modo_design: template.modo_design || 'blocos',
        canvas: template.canvas,
        itens_padrao: template.itens_padrao,
        validade_dias_default: template.validade_dias_default,
        config_automacao: template.config_automacao,
        mensagens_ia: template.mensagens_ia || []
      };
      const response = isEditing
        ? await propostasService.editarTemplate(id, payload)
        : await propostasService.criarTemplate({ ...payload, cliente_id: user?.clienteId });

      if (response.error) {
        toast({ title: 'Erro', description: response.message || 'Não foi possível salvar o modelo', variant: 'destructive' });
        return;
      }
      toast({ title: 'Sucesso', description: isEditing ? 'Modelo atualizado' : 'Modelo criado' });
      navigate(`/dashboard/propostas/modelos/${response.data._id}/editar`, { replace: true });
    } catch (error) {
      console.error('Erro ao salvar modelo:', error);
      toast({ title: 'Erro', description: 'Não foi possível salvar o modelo', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-gray-400">Carregando modelo...</span>
      </div>
    );
  }

  return (
    <div className={PROPOSTAS_DESIGN.editor.page}>
      <div className={PROPOSTAS_DESIGN.editor.topbar}>
        <div className={PROPOSTAS_DESIGN.editor.topbarLeft}>
          <button className={PROPOSTAS_DESIGN.editor.backButton} onClick={() => navigate('/dashboard/propostas?tab=modelos')}>
            <Iconify icon="eva:arrow-back-outline" width={20} />
          </button>
          <h1 className={PROPOSTAS_DESIGN.editor.title}>{isEditing ? 'Editar Modelo' : 'Novo Modelo'}</h1>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md flex items-center gap-1.5 flex-shrink-0 ${modoCanvas ? 'bg-violet-50 text-[#7C3AED] dark:bg-violet-500/10' : 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${modoCanvas ? 'bg-[#7C3AED]' : 'bg-gray-400'}`} />
            {modoCanvas ? 'Design livre' : 'Blocos'}
          </span>
          {template.padrao && <span className={PROPOSTAS_DESIGN.editor.badge}>PADRÃO</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className={`h-9 rounded-lg px-4 text-sm font-medium border-gray-200 dark:border-[#383838] ${modoCanvas ? 'bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED] border-violet-200 dark:border-violet-500/30' : 'dark:text-[#D1D2D3]'}`}
            onClick={handleToggleDesignMode}
            title={modoCanvas ? 'Usar o formato em blocos' : 'Usar o design livre (arrastar e soltar)'}
          >
            <Iconify icon={modoCanvas ? 'mdi:view-agenda-outline' : 'mdi:cursor-move'} width={16} className="mr-2" />
            {modoCanvas ? 'Usar blocos' : 'Usar design livre'}
          </Button>

          <Button
            variant="outline"
            className={`h-9 rounded-lg px-4 text-sm font-medium border-gray-200 dark:border-[#383838] ${previewMode ? 'bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED] border-violet-200 dark:border-violet-500/30' : 'dark:text-[#D1D2D3]'}`}
            onClick={() => setPreviewMode(v => !v)}
          >
            <Iconify icon="mdi:eye-outline" width={16} className="mr-2" />
            Pré-visualizar
          </Button>

          <Button
            className="h-9 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium rounded-lg px-4 text-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <Iconify icon="mdi:loading" className="mr-2 animate-spin" width={16} />}
            {isEditing ? 'Salvar' : 'Criar Modelo'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Coluna esquerda: configurações do modelo — ou a conversa com o agente,
            alargada, quando o modo conversa está aberto (igual ao editor de proposta) */}
        <aside className={`${chatAberto ? 'w-[440px]' : 'w-[272px]'} flex-shrink-0 border-r border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] overflow-hidden transition-all duration-300`}>
          {chatAberto ? (
            <div className="h-full flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100 dark:border-[#383838] flex-shrink-0">
                <h3 className="text-xs font-bold text-gray-900 dark:text-[#D1D2D3] uppercase tracking-wider flex items-center gap-1.5">
                  <Iconify icon="mdi:creation" width={14} className="text-[#7C3AED]" />
                  Agente de IA
                </h3>
                <button
                  type="button"
                  onClick={() => setChatAberto(false)}
                  title="Fechar conversa"
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#383838] text-gray-400 hover:text-gray-600 dark:hover:text-[#D1D2D3] transition-colors"
                >
                  <Iconify icon="eva:close-outline" width={16} />
                </button>
              </div>
              <div className="px-4 pt-3 flex-shrink-0">
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Peça as seções, os itens padrão e o visual do modelo. O agente usa variáveis
                  como <code className="bg-gray-100 dark:bg-[#1A1D21] px-1 rounded">{'{{contato.nome}}'}</code> no lugar de dados de um cliente específico.
                </p>
              </div>
              <div className="flex-1 min-h-0 flex flex-col px-4 pb-4 pt-3">
                <AgenteIaPainel
                  mensagens={template.mensagens_ia}
                  onEnviar={handleGerarComIa}
                  mensagemVazia="Descreva abaixo o modelo que você quer montar (seções, itens padrão, tom e visual)."
                  placeholder="Ex.: modelo de proposta para consultoria, com escopo, cronograma e 3 itens padrão..."
                  autoFocus
                />
              </div>
            </div>
          ) : (
          <div className="p-4 space-y-4 h-full overflow-y-auto">
            <h3 className="text-xs font-bold text-gray-900 dark:text-[#D1D2D3] uppercase tracking-wider flex items-center gap-1.5">
              <Iconify icon="mdi:cog-outline" width={14} className="text-[#7C3AED]" />
              Configurações
            </h3>

            <div>
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1.5 block">Nome do modelo</span>
              <Input value={template.nome || ''} onChange={e => setTemplate(prev => ({ ...prev, nome: e.target.value }))} className="h-9 text-sm rounded-xl" />
            </div>

            <div>
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1.5 block">Categoria</span>
              <Select value={template.categoria || 'geral'} onValueChange={(v) => setTemplate(prev => ({ ...prev, categoria: v }))}>
                <SelectTrigger className="h-9 text-sm rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS_MODELO.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1.5 block">Validade padrão (dias)</span>
              <Input type="number" value={template.validade_dias_default || 15} onChange={e => setTemplate(prev => ({ ...prev, validade_dias_default: Number(e.target.value) }))} className="h-9 text-sm rounded-xl" />
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                className="mt-0.5"
                checked={Boolean(template.config_automacao?.ao_aceitar?.atualizar_valor_lead)}
                onCheckedChange={(checked) => setTemplate(prev => ({ ...prev, config_automacao: { ...prev.config_automacao, ao_aceitar: { ...prev.config_automacao?.ao_aceitar, atualizar_valor_lead: Boolean(checked) } } }))}
              />
              <span className="text-xs text-gray-600 dark:text-[#ABABAD] leading-snug">Ao aceitar, atualizar o valor da oportunidade no CRM</span>
            </div>

            <div className="pt-3 border-t border-gray-100 dark:border-[#383838]">
              <button
                type="button"
                onClick={() => setChatAberto(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
              >
                <Iconify icon="mdi:creation" width={16} />
                Conversar com o agente
                {template.mensagens_ia?.length > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px] font-semibold leading-none">
                    {template.mensagens_ia.length}
                  </span>
                )}
              </button>
            </div>

            <div className="pt-3 border-t border-gray-100 dark:border-[#383838]">
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Monte o modelo com os blocos que quiser. Nos blocos de texto, use variáveis como <code className="bg-gray-100 dark:bg-[#1A1D21] px-1 rounded">{'{{contato.nome}}'}</code> e <code className="bg-gray-100 dark:bg-[#1A1D21] px-1 rounded">{'{{lead.nome}}'}</code> — serão preenchidas ao criar a proposta.
              </p>
            </div>
          </div>
          )}
        </aside>

        {/* Centro: modelo em edição */}
        {modoCanvas ? (
          <CanvasEditor proposta={proposta} onChange={handleChangeCanvas} ocultarPaleta={chatAberto} />
        ) : (
          <div className="flex-1 min-w-0 overflow-y-auto py-8 px-4">
            <DocumentCanvas proposta={proposta} onChange={handleChangeCanvas} readOnly={false} isModelo={true} />
          </div>
        )}

        {/* Direita: pré-visualização (desliza) */}
      </div>

      {previewMode && (
        <VisualizadorDocumento
          proposta={proposta}
          modoCanvas={modoCanvas}
          isModelo
          titulo={template.nome || 'Modelo'}
          subtitulo="As variáveis viram dados reais na proposta"
          onClose={() => setPreviewMode(false)}
        />
      )}

    </div>
  );
}
